// apps/web/src/components/chat/forms/TrialProcessingForm.tsx
import {
  Button,
  Typography,
  Box,
  Paper,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { createClient } from '@lingaai/supabase-client';

interface TrialProcessingData {
  submission_id: string;
  company_name: string;
  period: string;
  status: string;
}

interface TrialProcessingFormProps {
  data: TrialProcessingData;
  tenantId: string;
  onSuccess: (result: { action: string; data?: any }) => void;
  onClose: () => void;
}

export function TrialProcessingForm({ data, tenantId, onSuccess, onClose }: TrialProcessingFormProps) {
  const [status, setStatus] = useState<string>(data.status);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Create Supabase client
  const supabase = createClient();

  // Fetch results and transition to TrialResultsForm
  const fetchResultsAndTransition = useCallback(async () => {
    try {
      // Fetch metrics first
      const { data: metrics, error: metricsError } = await supabase
        .from('metrics')
        .select('id, value, confidence, source_location, metric_definition_id')
        .eq('submission_id', data.submission_id)
        .eq('tenant_id', tenantId);

      if (metricsError) {
        console.error('Failed to fetch metrics:', metricsError);
        setError(`Failed to load results: ${metricsError.message}`);
        return;
      }

      // Fetch metric definitions separately
      const metricDefIds = metrics?.map((m: any) => m.metric_definition_id).filter(Boolean) || [];

      let metricDefs: any[] = [];
      if (metricDefIds.length > 0) {
        const { data: defs, error: defsError } = await supabase
          .from('metric_definitions')
          .select('id, name, description, data_type')
          .in('id', metricDefIds);

        if (defsError) {
          console.error('Failed to fetch metric definitions:', defsError);
          // Continue anyway with Unknown names
        } else {
          metricDefs = defs || [];
        }
      }

      // Create a map for quick lookup
      const defMap = new Map(metricDefs.map((d: any) => [d.id, d]));

      // Format metrics for TrialResultsForm
      const formattedMetrics = (metrics || []).map((m: any) => {
        const def = defMap.get(m.metric_definition_id);
        return {
          name: def?.name || 'Unknown Metric',
          value: m.value,
          confidence: m.confidence,
          source_location: m.source_location,
        };
      });

      // Transition to results form
      onSuccess({
        action: 'show_results',
        data: {
          submission_id: data.submission_id,
          company_name: data.company_name,
          period: data.period,
          metrics_extracted: formattedMetrics.length,
          metrics: formattedMetrics,
        },
      });
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(`Failed to load results: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [data.submission_id, data.company_name, data.period, tenantId, supabase, onSuccess]);

  // Poll for submission status
  const checkStatus = useCallback(async () => {
    try {
      const { data: submission, error: submissionError } = await supabase
        .from('submissions')
        .select('status, completeness')
        .eq('id', data.submission_id)
        .eq('tenant_id', tenantId)
        .single();

      if (submissionError) {
        console.error('Failed to fetch submission status:', submissionError);
        return;
      }

      if (!submission) return;

      setStatus(submission.status);

      // Update progress based on status
      if (submission.status === 'processing') {
        // Estimate progress (smooth animation)
        setProgress((prev) => Math.min(prev + 5, 95));
      } else if (submission.status === 'completed') {
        setProgress(100);
        // Fetch extracted metrics and transition to results
        await fetchResultsAndTransition();
      } else if (submission.status === 'failed') {
        setError('Extraction failed. Please try again.');
        setProgress(0);
      }
    } catch (err) {
      console.error('Error checking status:', err);
    }
  }, [data.submission_id, tenantId, fetchResultsAndTransition, supabase]);

  // Poll every 3 seconds
  useEffect(() => {
    if (status === 'processing') {
      const interval = setInterval(checkStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [status, checkStatus]);

  // Subscribe to realtime updates for faster feedback
  useEffect(() => {
    const channel = supabase
      .channel(`submission:${data.submission_id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'submissions',
          filter: `id=eq.${data.submission_id}`,
        },
        (payload: any) => {
          if (payload.new && 'status' in payload.new) {
            setStatus(payload.new.status as string);
            if (payload.new.status === 'completed') {
              setProgress(100);
              fetchResultsAndTransition();
            } else if (payload.new.status === 'failed') {
              setError('Extraction failed. Please try again.');
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [data.submission_id, tenantId, fetchResultsAndTransition]);

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="h6">Extracting Metrics...</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Company Info */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Company
          </Typography>
          <Typography variant="body1">{data.company_name}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Period
          </Typography>
          <Typography variant="body1">{data.period}</Typography>
        </Box>
      </Paper>

      {/* Progress */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Analyzing document...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          This typically takes 1-2 minutes. Please wait while we analyze your document.
        </Typography>
      </Box>

      {/* Status Messages */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight="medium">
            What's happening:
          </Typography>
        </Box>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary">
            Reading and analyzing your document
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Extracting financial metrics and data points
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Verifying accuracy and confidence scores
          </Typography>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={status === 'processing'}>
          Close
        </Button>
      </Box>
    </Box>
  );
}
